import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export enum CashOpenType {
    WITH_ORDER = 'WITH_ORDER',
    WITHOUT_ORDER = 'WITHOUT_ORDER'
}

export enum CashOpenOrigin {
    BUTTON = 'BUTTON',
    SHORTCUT = 'SHORTCUT',
    ORDER_CREATION = 'ORDER_CREATION',
    ORDER_VALIDATION = 'ORDER_VALIDATION'
}

export interface CashJournal {
    id?: number;
    type: CashOpenType;
    orderId?: number;
    username: string;
    timestamp?: Date;
    workstation?: string;
    origin: CashOpenOrigin;
    reason?: string;
    comment?: string;
    amount?: number;
    reconciliationStatus?: string;
    linkedOrderIds?: string;
    isOrderVirtual?: boolean;
}

export interface CashJournalPageResponse {
    content: CashJournal[];
    totalPages: number;
    totalElements: number;
    size: number;
    number: number;
}

export interface CashHistoryStats {
    total: number;
    today: number;
    withOrder: number;
    withoutOrder: number;
    shortcuts: number;
    virtual: number;
}

@Injectable({
    providedIn: 'root'
})
export class CashJournalService {

    private apiUrl = environment.apiUrl + '/cash-journal';

    constructor(private http: HttpClient) { }

    logEvent(journal: CashJournal): Observable<CashJournal> {
        return this.http.post<CashJournal>(this.apiUrl, journal);
    }

    getAllEvents(): Observable<CashJournal[]> {
        return this.http.get<CashJournal[]>(this.apiUrl);
    }

    getHistoryPage(page: number, size: number, filters: any): Observable<CashJournalPageResponse> {
        let params: any = {
            page: page.toString(),
            size: size.toString()
        };
        if (filters.type) params.type = filters.type;
        if (filters.origin) params.origin = filters.origin;
        if (filters.username) params.username = filters.username;
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;

        return this.http.get<CashJournalPageResponse>(`${this.apiUrl}/page`, { params });
    }

    getHistoryStats(): Observable<CashHistoryStats> {
        return this.http.get<CashHistoryStats>(`${this.apiUrl}/stats`);
    }

    /**
     * Get a reconciliation report for a specific date.
     * @param mode 'dashboard' for fast loading (summary only), 'workbench' for full detail.
     */
    getReconciliationReport(date?: string, mode: 'dashboard' | 'workbench' = 'workbench'): Observable<any> {
        let url = `${this.apiUrl}/reconciliation-report?mode=${mode}`;
        if (date) {
            url += `&date=${date}`;
        }
        return this.http.get<any>(url);
    }

    /**
     * Confirm a suggested match between one or more CashJournal entries and orders.
     */
    confirmMatch(ids: number | number[], orderIds: number[]): Observable<any> {
        if (Array.isArray(ids)) {
            // New multi-ID endpoint (fallback to first if multi not matching backend exactly yet)
            return this.http.post(`${this.apiUrl}/confirm-match-multi`, { cashJournalIds: ids, orderIds: orderIds }, { responseType: 'text' });
        }
        return this.http.post(`${this.apiUrl}/${ids}/confirm-match`, orderIds, { responseType: 'text' });
    }

    /**
     * Crée un F8 et le lie immédiatement à une commande en un seul appel réseau.
     * Remplace logEvent() + confirmMatch() séparés.
     */
    logAndLink(orderId: number, amount: number, username: string, reason: string): Observable<CashJournal> {
        return this.http.post<CashJournal>(`${this.apiUrl}/log-and-link`, {
            orderId, amount, username, reason
        });
    }

    getUnconfirmedCount(): Observable<number> {
        return this.http.get<number>(`${this.apiUrl}/unconfirmed-count`);
    }

    getAvailableVirtualBalance(): Observable<number> {
        return this.http.get<number>(`${this.apiUrl}/available-virtual-balance`);
    }

    /**
     * Update an F8 entry (admin only).
     * Backend breaks reconciliation group automatically if needed.
     */
    updateF8(id: number, updates: Partial<CashJournal>): Observable<CashJournal> {
        return this.http.put<CashJournal>(`${this.apiUrl}/${id}`, updates);
    }

    /**
     * Delete an F8 entry (admin only).
     * Backend breaks reconciliation group automatically if needed.
     */
    deleteF8(id: number): Observable<string> {
        return this.http.delete(`${this.apiUrl}/${id}`, { responseType: 'text' });
    }

    /**
     * Force a manual link between F8s and orders — used by Reconciliation Workbench.
     * Bypasses amount validation (for partial payments, operator-verified matches, etc.)
     */
    forceLink(f8Ids: number[], orderIds: number[]): Observable<string> {
        return this.http.post(`${this.apiUrl}/force-link`,
            { cashJournalIds: f8Ids, orderIds: orderIds },
            { responseType: 'text' }
        );
    }
}
